import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { STATS, STAT_LABEL } from "@/lib/format";

const signed = (v) => `${v > 0 ? "+" : ""}${v.toFixed(2)}`;
const biasTone = (v, avg) => (Math.abs(v) < Math.max(0.15, avg * 0.05) ? "" : v > 0 ? "is-over" : "is-under");

function GroupTable({ groups }) {
  return (
    <div className="aa-table-scroll">
      <table className="aa-table" data-testid="mr-groups-table">
        <thead><tr><th>Expected minutes</th><th>Player games</th>{STATS.map((s) => <th key={s}>{STAT_LABEL[s]} error · bias</th>)}</tr></thead>
        <tbody>{groups.map((g) => (
          <tr key={g.key} data-testid={`mr-group-${g.key}`}>
            <td>{g.label}</td><td>{g.stats.pts?.n ?? 0}</td>
            {STATS.map((s) => {
              const x = g.stats[s];
              return <td key={s}>{x ? <>{x.mae.toFixed(2)} · <b className={`mr-bias ${biasTone(x.bias, x.avg)}`}>{signed(x.bias)}</b></> : "—"}</td>;
            })}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function RotationCheck({ check, title, testId }) {
  const factors = Object.entries(check.factors);
  const n = factors[0]?.[1]?.pts?.n || 0;
  return (
    <div className="mr-check" data-testid={testId}>
      <h3>{title}</h3>
      {!n ? <p className="aa-empty">No rotation games since {check.since} yet. This fills in as new games are played.</p> : (
        <table className="aa-table">
          <thead><tr><th>Factor</th>{STATS.map((s) => <th key={s}>{STAT_LABEL[s]} error · bias</th>)}</tr></thead>
          <tbody>{factors.map(([f, st]) => (
            <tr key={f}><td>{f === "x1.00" ? "x1.00 (no factor)" : `${f} (active)`}</td>
              {STATS.map((s) => <td key={s}>{st[s] ? `${st[s].mae.toFixed(2)} · ${signed(st[s].bias)}` : "—"}</td>)}</tr>
          ))}</tbody>
        </table>
      )}
      {n > 0 && <p className="aa-explainer">{n} rotation player games since {check.since}. Lower error = better; bias near 0 = balanced.</p>}
    </div>
  );
}

// Error and bias of the current model per expected-minutes group; rebuilt daily on the server.
export function MinutesGroupReport() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true, t;
    const load = () => api.get("/model-report").then((r) => {
      if (!alive) return;
      setD(r.data);
      if (!r.data.daily) t = setTimeout(load, 8000);
    }).catch(() => {});
    load();
    return () => { alive = false; clearTimeout(t); };
  }, []);
  if (!d) return null;
  const r = d.daily;
  return (
    <section className="aa-section" data-testid="model-report">
      <h2>Model report · by expected minutes</h2>
      <p className="aa-explainer">Every game this season re-projected with the current model, using only the games before it. Grouped by the minutes we expected (this season's average). Bias above 0 = we projected too high. Updated daily at 09:00.</p>
      {!r ? <p className="aa-empty" data-testid="mr-building">Building the first report… this takes under a minute.</p> : (
        <>
          <GroupTable groups={r.groups} />
          <p className="aa-explainer" data-testid="mr-built-at">{r.games} player games · updated {new Date(r.built_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
          <RotationCheck check={r.rotation_check} title="Rotation factor check (15–29 min players)" testId="mr-rotation-check" />
          {d.recheck && <RotationCheck check={d.recheck.rotation_check} testId="mr-recheck"
            title={`2-week recheck · ${new Date(d.recheck.built_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`} />}
        </>
      )}
    </section>
  );
}
